/**
 * Product Gallery Custom Element
 * Varyant değişiminde Section Rendering API ile gallery reload + Fancybox yönetimi
 *
 * theme.js'deki ProductVariants sınıfı 'variant:changed' event'i dispatch eder.
 * Bu element gallery'yi yeni varyantın görselleriyle yeniden yükler.
 */

class ProductGallery extends HTMLElement {
  constructor() {
    super();
    this.currentVariantId = this.dataset.variantId;
    this.variantSlideMap = this.#parseVariantSlideMap();
    this.sectionId = this.dataset.sectionId || this.#getSectionIdFromParent();
    this.productUrl = this.dataset.productUrl || window.location.pathname;
    this.reloadController = null;
  }

  connectedCallback() {
    this.#listenToVariantEvents();
    this.#initThumbnailScroll();
    requestAnimationFrame(() => {
      this.#initFancybox();
    });
  }

  disconnectedCallback() {
    this.#removeEventListeners();
    this.#destroyFancybox();
  }

  #removeEventListeners() {
    if (this.variantChangeHandler) {
      document.removeEventListener('variant:changed', this.variantChangeHandler);
    }
  }

  #listenToVariantEvents() {
    this.variantChangeHandler = (e) => {
      const variant = e.detail?.variant;
      if (!variant) return;

      const variantId = String(variant.id);
      if (variantId === this.currentVariantId) return;

      this.currentVariantId = variantId;
      this.#reloadGallerySection(variantId);
    };

    document.addEventListener('variant:changed', this.variantChangeHandler);
  }

  async #reloadGallerySection(variantId) {
    if (this.reloadController) {
      this.reloadController.abort();
    }
    this.reloadController = new AbortController();

    const url = `${this.productUrl}?variant=${variantId}&section_id=${this.sectionId}`;

    try {
      const response = await fetch(url, { signal: this.reloadController.signal });
      if (!response.ok) return;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newGallery = doc.querySelector('n-product-gallery');
      if (!newGallery) return;

      this.#destroyFancybox();

      this.innerHTML = newGallery.innerHTML;
      this.dataset.variantId = variantId;
      this.dataset.variantSlides = newGallery.dataset.variantSlides || '{}';
      this.variantSlideMap = this.#parseVariantSlideMap();

      this.#initThumbnailScroll();

      requestAnimationFrame(() => {
        this.#initFancybox();
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('[ProductGallery] Section reload failed:', err);
      }
    }
  }

  #parseVariantSlideMap() {
    try {
      const raw = this.dataset.variantSlides;
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  #getSectionIdFromParent() {
    const section = this.closest('[data-section-id]');
    return section ? section.dataset.sectionId : '';
  }

  #slideToVariant(variantId) {
    const slideIndex = this.variantSlideMap[variantId];
    if (slideIndex === undefined) return;

    const slider = this.querySelector('n-responsive-slider');
    if (!slider) return;

    setTimeout(() => {
      slider.goToSlide(slideIndex);
      slider.updateActiveThumbnail(slideIndex);
    }, 100);
  }

  #initThumbnailScroll() {
    const scroller = this.querySelector('.product-gallery__thumbnial');
    const scrollBtn = this.querySelector('.product-gallery__scroll-btn');
    if (!scroller || !scrollBtn) return;

    const updateScrollButton = () => {
      const isAtBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 10;
      scrollBtn.style.display = isAtBottom ? 'none' : 'flex';
    };

    scrollBtn.addEventListener('click', () => {
      scroller.scrollBy({ top: 120, behavior: 'smooth' });
    });

    scroller.addEventListener('scroll', updateScrollButton);
    updateScrollButton();
  }

  #initFancybox() {
    if (typeof Fancybox === 'undefined') {
      setTimeout(() => this.#initFancybox(), 100);
      return;
    }

    const fancyboxElements = this.querySelectorAll('[data-fancybox="product-images"]');
    if (fancyboxElements.length === 0) return;

    this.fancyboxInstance = Fancybox.bind('[data-fancybox="product-images"]', {
      zoomEffect: true,
      fadeEffect: true,
      hideScrollbar: true,
      dragToClose: true,
      Toolbar: {
        display: {
          left: [],
          middle: [],
          right: ['close']
        }
      },
      keyboard: {
        Escape: 'close',
        Delete: 'close',
        Backspace: 'close',
        PageUp: 'next',
        PageDown: 'prev',
        ArrowUp: 'prev',
        ArrowDown: 'next',
        ArrowRight: 'next',
        ArrowLeft: 'prev',
      },
      Carousel: {
        infinite: false,
        Toolbar: {
          display: {
            left: [],
            middle: [],
            right: ['close']
          }
        }
      },
      Thumbs: {
        type: 'classic',
        autoStart: true,
      },
    });
  }

  #destroyFancybox() {
    if (typeof Fancybox === 'undefined') return;

    const instances = Fancybox.getInstance();
    if (instances) {
      instances.close();
    }

    Fancybox.unbind('[data-fancybox="product-images"]');
    Fancybox.close();
  }
}

customElements.define('n-product-gallery', ProductGallery);
